/*
 * Módulo de Depreciación de Activos Fijos.
 *
 * Único punto de contacto con `/api/v1/depreciation/*`. El backend es la fuente
 * de verdad del cálculo: aquí NO se deprecia nada, sólo se arman los cuerpos de
 * la petición y se normaliza el error para que la pantalla pueda mostrar el
 * `message` que el servidor ya redactó para el usuario final.
 *
 * SIN CACHÉ, por la misma razón que los reportes: contabilizar o anular cambia
 * la contabilidad y una copia vieja mostraría una depreciación que ya no existe.
 */

const BASE = "/depreciation";

// Claves que acepta el cuerpo de /preview y /generate. El backend responde 400
// ante cualquier otra, así que el payload se arma con esta lista y no con lo que
// traiga el formulario.
const RUN_KEYS = [
  "period",
  "books",
  "asset_codes",
  "group_codes",
  "detail_level",
  "prefix",
  "notes"
];

// La pantalla ya no ofrece libros, nivel de detalle ni notas: la depreciación es
// la que sale de la configuración del activo, consolidada. Estos valores se
// siguen enviando SÓLO porque el contrato actual los exige; cuando el backend
// los retire, salen de RUN_KEYS y de aquí.
const FIXED_BOOKS = ["accounting"];
const FIXED_DETAIL_LEVEL = "consolidated";

/**
 * Normaliza el error de axios a algo que la vista pueda pintar sin volver a
 * hurgar en `err.response`. Se conserva `info` completo porque en el 409 de
 * /generate ahí viajan los `skipped`/`errors` que explican POR QUÉ no había
 * nada que depreciar — justo lo que el usuario necesita ver.
 */
function extractApiError(err) {
  const response = err && err.response;
  const body = response && response.data;
  if (!body) {
    const network = new Error(
      (err && err.message) || "No se pudo contactar el servicio de depreciación."
    );
    network.statusCode = response ? response.status : null;
    network.validationErrors = null;
    network.info = null;
    return network;
  }
  const enriched = new Error(body.message || "Error en el proceso de depreciación.");
  enriched.statusCode = response.status;
  // `info.errors` significa dos cosas distintas según el caso: en el 400 de
  // validación es una lista de textos, y en el 409 de /generate es una lista de
  // activos ({ assetCode, reasons }). Aquí sólo se recogen los textos; los
  // activos siguen disponibles en `info` para pintarlos como corresponde.
  const rawErrors = body.info && Array.isArray(body.info.errors) ? body.info.errors : [];
  const messages = rawErrors.filter(e => typeof e === "string");
  enriched.validationErrors = messages.length ? messages : null;
  enriched.info = body.info || null;
  return enriched;
}

// Lista de códigos → arreglo, o null cuando no hay filtro ("todos"). Se manda
// null y no [] a propósito: un arreglo vacío es un filtro que no deja pasar nada.
function codeList(value) {
  if (value == null || value === "") return null;
  const arr = Array.isArray(value) ? value : String(value).split(",");
  const clean = arr
    .map(v => (v == null ? "" : String(v).trim()))
    .filter(v => v !== "");
  return clean.length ? clean : null;
}

function buildRunPayload(params = {}) {
  const payload = {
    period: params.period,
    books: FIXED_BOOKS,
    asset_codes: codeList(params.asset_codes),
    group_codes: codeList(params.group_codes),
    detail_level: FIXED_DETAIL_LEVEL,
    prefix: params.prefix ? String(params.prefix).trim() : null,
    notes: null
  };
  // Blindaje contra el 400 por clave desconocida: sólo sale lo documentado.
  return RUN_KEYS.reduce((acc, key) => {
    acc[key] = payload[key];
    return acc;
  }, {});
}

async function callApi(axios, url, body) {
  try {
    const response = await axios.post(url, body);
    return response && response.data !== undefined ? response.data : response;
  } catch (err) {
    throw extractApiError(err);
  }
}

const state = () => ({
  // Último periodo consultado. Vive en el store para que al volver a la
  // pantalla (o al saltar entre pestañas) no haya que volver a elegirlo.
  lastPeriod: null
});

const mutations = {
  SET_DEPRECIATION_PERIOD(state, period) {
    state.lastPeriod = period || null;
  }
};

const actions = {
  async depreciationPreview({ commit }, params = {}) {
    commit("SET_DEPRECIATION_PERIOD", params.period);
    return callApi(this.$axios, `${BASE}/preview`, buildRunPayload(params));
  },

  async depreciationGenerate({ commit }, params = {}) {
    commit("SET_DEPRECIATION_PERIOD", params.period);
    return callApi(this.$axios, `${BASE}/generate`, buildRunPayload(params));
  },

  /**
   * Contabiliza en lote. Responde 200 aunque parte del lote falle: quien llama
   * DEBE mirar `posted` y `failed`, no sólo `success`.
   */
  async depreciationPost(_ctx, docIds = []) {
    return callApi(this.$axios, `${BASE}/post`, { docIds: docIds.map(String) });
  },

  /** Anula (borra los asientos). Misma semántica de éxito parcial que /post. */
  async depreciationReverse(_ctx, docIds = []) {
    return callApi(this.$axios, `${BASE}/reverse`, { docIds: docIds.map(String) });
  },

  async depreciationSchedule(_ctx, { assetCode, fromPeriod = null, toPeriod = null } = {}) {
    return callApi(this.$axios, `${BASE}/schedule`, {
      asset_code: assetCode,
      book: FIXED_BOOKS[0],
      from_period: fromPeriod || null,
      to_period: toPeriod || null
    });
  },

  async depreciationRuns(_ctx, { period = null } = {}) {
    return callApi(this.$axios, `${BASE}/runs`, {
      period: period || null,
      books: null
    });
  }
};

const getters = {
  depreciationLastPeriod: state => state.lastPeriod
};

export default { state, mutations, actions, getters };
